/* Hook de plantão médico — espelha shape da extensão Dr. Pera (brief v4.0 §7.1)
 * Persiste em localStorage com key `dr_pera_plantao` (mesmo nome lógico da ext)
 * Inclui: plantao_id (formato YYYY-MM-DD-HHh), inicio, casos[], consultas_ia, creditos_usados
 * Backend lê com mesma shape da extensão — zero adaptação no servidor.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import type { PlantaoState, CasoPlantao, PlantaoPersistido } from '../types';

/** Chave de persistência no localStorage (web app) */
const STORAGE_KEY = 'dr_pera_plantao';

/** Duração máxima do plantão: 12 horas em ms */
const DURACAO_PLANTAO_MS = 12 * 60 * 60 * 1000;

/** Formata "YYYY-MM-DD-HHh" a partir de timestamp ms (hora local) */
function formatPlantaoId(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}h`;
}

/** Formata "HH:mm" a partir de timestamp ms (hora local) */
function formatHoraCaso(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Formata duração em horas/minutos para o contexto */
function formatarDuracao(ms: number): string {
  const totalMin = Math.floor(ms / 60000);
  const horas = Math.floor(totalMin / 60);
  const minutos = totalMin % 60;
  if (horas === 0) return `${minutos}min`;
  return `${horas}h ${minutos}min`;
}

/** Gera string de contexto resumido para o backend (não usado no shape PlantaoContext, só pra UX) */
function gerarContexto(inicio: number): string {
  const decorrido = Date.now() - inicio;
  const horaInicio = new Date(inicio).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `PLANTÃO ATIVO desde ${horaInicio}. Tempo decorrido: ${formatarDuracao(decorrido)}.`;
}

/** Constrói plantão inicial vazio */
function buildPlantaoInicial(inicio: number): PlantaoPersistido {
  return {
    plantao_id: formatPlantaoId(inicio),
    inicio,
    duracao_max: DURACAO_PLANTAO_MS,
    casos: [],
    consultas_ia: 0,
    creditos_usados: 0,
  };
}

/** Lê plantão do localStorage. Retorna null se não existir ou expirado. */
function lerPlantaoStorage(): PlantaoPersistido | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const dados = JSON.parse(raw) as PlantaoPersistido;
    if (!dados.plantao_id || typeof dados.inicio !== 'number') return null;
    if (Date.now() - dados.inicio >= (dados.duracao_max ?? DURACAO_PLANTAO_MS)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return dados;
  } catch {
    return null;
  }
}

/** Salva plantão no localStorage */
function salvarPlantaoStorage(plantao: PlantaoPersistido): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(plantao));
  } catch {
    // silencioso
  }
}

/**
 * Hook de plantão médico — espelha extensão.
 * Persiste estado completo (casos + contadores) em localStorage.
 * Auto-encerra após 12h.
 */
export function usePlantao(): PlantaoState {
  const [plantao, setPlantao] = useState<PlantaoPersistido | null>(() => lerPlantaoStorage());
  const plantaoRef = useRef<PlantaoPersistido | null>(plantao);
  plantaoRef.current = plantao;

  const [contexto, setContexto] = useState<string | null>(() =>
    plantao ? gerarContexto(plantao.inicio) : null,
  );

  /** Atualiza contexto a cada minuto + auto-encerra ao expirar */
  useEffect(() => {
    if (!plantao) {
      setContexto(null);
      return;
    }
    setContexto(gerarContexto(plantao.inicio));

    const intervalId = setInterval(() => {
      const atual = plantaoRef.current;
      if (!atual) return;
      if (Date.now() - atual.inicio >= atual.duracao_max) {
        localStorage.removeItem(STORAGE_KEY);
        plantaoRef.current = null;
        setPlantao(null);
        setContexto(null);
        return;
      }
      setContexto(gerarContexto(atual.inicio));
    }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [plantao?.inicio]);

  /** Sync entre abas */
  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (event.key !== STORAGE_KEY) return;
      if (event.newValue) {
        try {
          const dados = JSON.parse(event.newValue) as PlantaoPersistido;
          plantaoRef.current = dados;
          setPlantao(dados);
          setContexto(gerarContexto(dados.inicio));
        } catch {
          // ignore
        }
      } else {
        plantaoRef.current = null;
        setPlantao(null);
        setContexto(null);
      }
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const iniciar = useCallback(() => {
    const novo = buildPlantaoInicial(Date.now());
    plantaoRef.current = novo;
    setPlantao(novo);
    setContexto(gerarContexto(novo.inicio));
    salvarPlantaoStorage(novo);
  }, []);

  const fechar = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    plantaoRef.current = null;
    setPlantao(null);
    setContexto(null);
  }, []);

  /** Adiciona caso ao plantão ativo. Resumo idealmente <80 chars. No-op se plantão null. */
  const adicionarCaso = useCallback((resumo: string): void => {
    const atual = plantaoRef.current;
    if (!atual) return;
    const trimmed = resumo.trim();
    if (!trimmed) return;
    const caso: CasoPlantao = {
      hora: formatHoraCaso(Date.now()),
      resumo: trimmed.length > 80 ? trimmed.slice(0, 77) + '...' : trimmed,
    };
    const atualizado: PlantaoPersistido = {
      ...atual,
      casos: [...atual.casos, caso],
    };
    plantaoRef.current = atualizado;
    setPlantao(atualizado);
    salvarPlantaoStorage(atualizado);
  }, []);

  /** Incrementa contador de consultas IA + créditos usados. No-op se plantão null. */
  const incrementarConsultaIA = useCallback((creditos: number): void => {
    const atual = plantaoRef.current;
    if (!atual) return;
    const atualizado: PlantaoPersistido = {
      ...atual,
      consultas_ia: atual.consultas_ia + 1,
      creditos_usados: atual.creditos_usados + (creditos > 0 ? creditos : 0),
    };
    plantaoRef.current = atualizado;
    setPlantao(atualizado);
    salvarPlantaoStorage(atualizado);
  }, []);

  return {
    ativo: Boolean(plantao),
    plantaoId: plantao?.plantao_id ?? null,
    inicio: plantao?.inicio ?? null,
    casos: plantao?.casos ?? [],
    consultasIa: plantao?.consultas_ia ?? 0,
    creditosUsados: plantao?.creditos_usados ?? 0,
    contexto,
    iniciar,
    fechar,
    adicionarCaso,
    incrementarConsultaIA,
  };
}
